import { buildServiceURL } from '@pcln/common-request-functions'
import pclnRequest from '@pcln/request'
import config from 'isomorphic-config'
import cleanSlashes from './clean-slashes'
import {
  AirportCode,
  City,
  CityId,
  CityName,
  Location,
  LocationResponsePayload,
  PoiId,
  FlightsResponsePayload
} from './typeahead-fetch-types'

function isAirport(airport: AirportCode | CityName): airport is AirportCode {
  return (airport as AirportCode).airportCode !== undefined
}

function isPoi(poi: PoiId | CityId): poi is PoiId {
  return (poi as PoiId).poiId !== undefined
}

const cityNameTransform = (cityInfo?: City | null) => {
  if (!cityInfo) return ''

  const { countryCode, countryName, cityName, stateCode } = cityInfo
  const city = cityName ? `${cityName}, ` : ''
  const province = countryCode === 'CA' ? `${stateCode}, ` : ''
  const state = countryCode === 'US' ? stateCode : province
  const country = countryCode !== 'US' && countryName ? countryName : ''

  return `${city}${state}${country}`
}

const findLocationByPoiIdOrCityId = (
  res: LocationResponsePayload,
  byPoiId = false
): Location | City => {
  const exactMatch = res.exactMatch ?? {}
  const matchedCity = exactMatch.matchedCity ?? {}

  const displayText = byPoiId
    ? exactMatch.displayText ?? ''
    : cityNameTransform(matchedCity)
  return byPoiId
    ? { ...res.searchedLocation, type: 'POI' }
    : Object.assign(matchedCity, {
        id: matchedCity?.cityID,
        entered: '',
        fromSavedSearch: false,
        highlightedName: '',
        lat: matchedCity.latitude,
        lon: matchedCity.longitude,
        cityName: matchedCity.cityName,
        itemName: displayText,
        countryCode: matchedCity.countryCode,
        stateCode: matchedCity.stateCode,
        cityID: matchedCity.cityID,
        type: 'CITY'
      })
}

async function retailSearchApiRequest(
  location: PoiId | CityId
): Promise<City | Location | null> {
  const value = isPoi(location) ? location.poiId : location.cityId
  const { BOOTSTRAP_DC, BOOTSTRAP_ENV } = process.env
  const SERVICE_RESOLUTION_OPTIONS = { dc: BOOTSTRAP_DC, env: BOOTSTRAP_ENV }

  const HOTEL_SEARCH_SERVICE_BASE_URL =
    config.services.hotels.typeahead.search.url ||
    buildServiceURL(
      'htlsolrsearch',
      '/htlsolrsearch/hotels',
      SERVICE_RESOLUTION_OPTIONS
    )
  const url = `${cleanSlashes(
    HOTEL_SEARCH_SERVICE_BASE_URL
  )}/rtlsearch/${value}`
  try {
    const result = await pclnRequest({
      url,
      timeout: config.services.hotels.typeahead.search.timeout
    })
    return findLocationByPoiIdOrCityId(result?.data, isPoi(location))
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
  }

  return null
}

async function apiRequest(
  location: AirportCode | CityName
): Promise<Location | null> {
  const { BOOTSTRAP_DC, BOOTSTRAP_ENV } = process.env
  const SERVICE_RESOLUTION_OPTIONS = { dc: BOOTSTRAP_DC, env: BOOTSTRAP_ENV }

  const value = isAirport(location) ? location.airportCode : location.cityName
  const urlSuffix = isAirport(location) ? '/0/1/0/0/' : '/1/0/0/0'
  const HOTEL_SEARCH_SERVICE_BASE_URL =
    config.services.hotels.typeahead.search.url ||
    buildServiceURL(
      'htlsolrsearch',
      '/htlsolrsearch/hotels',
      SERVICE_RESOLUTION_OPTIONS
    )
  const url = `${cleanSlashes(
    HOTEL_SEARCH_SERVICE_BASE_URL
  )}/${value}/${cleanSlashes(urlSuffix)}`
  try {
    const result = await pclnRequest({
      url,
      timeout: config.services.hotels.typeahead.search.timeout
    })
    if (result?.data) {
      const { searchItems = [] } = result.data
      return searchItems?.[0] ?? null
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
  }

  return null
}

async function flightsLocationRequest(urls: string[]) {
  let locationItems
  try {
    locationItems = await Promise.all(
      urls.map((url: string) =>
        pclnRequest({ url }).then((result: FlightsResponsePayload) => {
          if (result.data) {
            const { searchItems = [] } = result.data
            if (searchItems.length) {
              return searchItems[0]
            }
            return null
          }
          return null
        })
      )
    )
  } catch (error) {
    locationItems = []
  }
  return locationItems
}

export {
  apiRequest,
  cityNameTransform,
  findLocationByPoiIdOrCityId,
  retailSearchApiRequest,
  flightsLocationRequest
}
